import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { getDefaultRouteForRole } from '../../utils/helpers';
import { subscriptionsAPI } from '../../services/api';
import { USER_ROLES } from '../../utils/constants';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check subscription status for establishment users
  useEffect(() => {
    const checkSubscription = async () => {
      // Skip subscription check for platform admins, sales users, and users without establishment
      if (!user || user.role === USER_ROLES.PLATFORM_ADMIN || user.role === USER_ROLES.SALES || !user.establishment_id) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const status = await subscriptionsAPI.getMyStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error checking subscription:', error);
        setSubscriptionStatus({ has_active_subscription: false });
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (isAuthenticated && user) {
      checkSubscription();
    } else {
      setCheckingSubscription(false);
    }
  }, [isAuthenticated, user]);

  // Show loading spinner while checking authentication or subscription
  if (isLoading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription status for non-platform admin and non-sales users
  if (user && user.role !== USER_ROLES.PLATFORM_ADMIN && user.role !== USER_ROLES.SALES && user.establishment_id) {
    // If no active subscription (not in trial and not active)
    if (subscriptionStatus && !subscriptionStatus.has_active_subscription) {
      // Establishment admin can access subscription page to manage it
      if (user.role === USER_ROLES.ESTABLISHMENT_ADMIN) {
        if (location.pathname !== '/subscription') {
          return <Navigate to="/subscription" replace />;
        }
      } else if (user.role === USER_ROLES.MANAGER) {
        // Manager cannot manage subscription, redirect to login with message
        // This shouldn't happen as backend blocks login, but just in case
        return <Navigate to="/login" state={{ message: 'Suscripción no activa. Contacte al administrador del establecimiento.' }} replace />;
      } else {
        // Other users cannot access anything without active subscription
        // This shouldn't happen as backend blocks login, but just in case
        return <Navigate to="/login" replace />;
      }
    }
  }

  // Check role permissions if allowedRoles is specified
  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    // Redirect to default route for the user's role instead of showing error
    const defaultRoute = getDefaultRouteForRole(user.role);
    return <Navigate to={defaultRoute} replace />;
  }

  return children;
};

export default ProtectedRoute;